import React, { useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Card } from "./ui/card";
import { MessageCircle, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";

const SHARE_TEMPLATES = [
  {
    id: "bank_run",
    title: "Bank Run — $60",
    icon: "🏦",
    color: "from-blue-600 to-indigo-700",
    url: "/bank-run",
    message: `🏦 BANK RUN — $60 cash 💵

Need to deposit a check, withdraw, or hit the ATM?
We'll drive you to any Madison bank from your Dells housing, wait ~20 min while you handle business, and bring you back.

✅ Round-trip
✅ Wait time included
✅ Up to 4 people for $60 flat
✅ Pay your driver cash — no card needed

📅 Book here 👉 {{URL}}

—Easy Transportation`,
  },
  {
    id: "airport_booking",
    title: "Airport Booking",
    icon: "✈️",
    color: "from-green-600 to-emerald-700",
    url: "/booking",
    message: `🚐 EASY TRANSPORTATION — Chicago & Milwaukee airports ↔ Wisconsin

✅ Door-to-door pickups
✅ Live flight tracking
✅ Per-person + private options
✅ WhatsApp support

🌐 Book online 👉 {{URL}}

—Easy Transportation`,
  },
  {
    id: "make_payment",
    title: "Make a Payment",
    icon: "💳",
    color: "from-amber-600 to-orange-700",
    url: "/make-payment",
    message: `💳 PAY YOUR BALANCE — Easy Transportation

If you owe a remaining balance on your trip, you can pay securely online with your confirmation number.

🔒 Stripe-secured
💵 Pay any amount

👉 {{URL}}

—Easy Transportation`,
  },
  {
    id: "group_booking",
    title: "Group Booking (5+)",
    icon: "👥",
    color: "from-yellow-500 to-amber-600",
    url: "/group-booking",
    message: `👥 GROUP BOOKING — 5 or more passengers?

Special rates and dedicated vehicles for groups travelling together.

✅ Best per-person price
✅ Single confirmation for everyone
✅ Door-to-door

📅 Book here 👉 {{URL}}

—Easy Transportation`,
  },
];

export const WhatsAppShareDialog = ({ buttonClassName = "", buttonTestId = "wa-share-btn" }) => {
  const [open, setOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const buildText = (tpl) => tpl.message.replace("{{URL}}", `${origin}${tpl.url}`);

  const copy = async (tpl) => {
    try {
      await navigator.clipboard.writeText(buildText(tpl));
      setCopiedId(tpl.id);
      toast.success("Copied to clipboard — paste into WhatsApp");
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      toast.error("Copy failed — long-press to copy on mobile");
    }
  };

  const shareToWhatsApp = (tpl) => {
    const text = encodeURIComponent(buildText(tpl));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={buttonClassName}
          data-testid={buttonTestId}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share on WhatsApp
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wa-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            Share on WhatsApp
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2 mb-3">
          Tap "Open WhatsApp" to forward the ready-made message to any contact, or copy to paste anywhere.
        </p>
        <div className="space-y-3">
          {SHARE_TEMPLATES.map((tpl) => (
            <Card key={tpl.id} className="overflow-hidden" data-testid={`wa-share-card-${tpl.id}`}>
              <div className={`bg-gradient-to-r ${tpl.color} text-white px-4 py-2 flex items-center gap-2`}>
                <span className="text-xl">{tpl.icon}</span>
                <span className="font-semibold flex-1">{tpl.title}</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{tpl.url}</span>
              </div>
              <pre className="px-4 py-3 text-xs whitespace-pre-wrap font-sans bg-gray-50 max-h-32 overflow-y-auto">
                {buildText(tpl)}
              </pre>
              <div className="flex gap-2 p-3 border-t bg-white">
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 flex-1"
                  onClick={() => shareToWhatsApp(tpl)}
                  data-testid={`wa-share-open-${tpl.id}`}
                >
                  <MessageCircle className="w-4 h-4 mr-1.5" /> Open WhatsApp
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(tpl)}
                  data-testid={`wa-share-copy-${tpl.id}`}
                >
                  {copiedId === tpl.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
